import { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Pencil } from "lucide-react";
import {
  BalanceAdjustmentRow,
  getBalanceAdjustmentProducts,
  getContestProductBalanceAdjustments,
} from "@/Api/balanceAdjustments";
import { CorrectBalanceModal } from "./CorrectBalanceModal";
import { FiltersCard } from "./FiltersCard";
import { TablePagination } from "./TablePagination";
import {
  ContestOption,
  ProductOption,
  pruneSelection,
  toOptions,
  toProductOptions,
} from "../types";

interface ContestProductBalanceFilters {
  contestIds: string[];
  productIds: string[];
}

const EMPTY_FILTERS: ContestProductBalanceFilters = {
  contestIds: [],
  productIds: [],
};

/**
 * Tab de balanço geral por concurso/bolão (contest_product_balances).
 * Campos: balance (balanço) e with_held_balance (valor preso).
 *
 * Os selects apenas montam a seleção; a listagem só é consultada ao clicar em
 * Filtrar, Atualizar ou ao trocar de página.
 */
export const ContestProductsTab = ({ contests }: { contests: ContestOption[] }) => {
  const [rows, setRows] = useState<BalanceAdjustmentRow[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  const [draftFilters, setDraftFilters] =
    useState<ContestProductBalanceFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] =
    useState<ContestProductBalanceFilters>(EMPTY_FILTERS);

  const [selectedRow, setSelectedRow] = useState<BalanceAdjustmentRow | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isDirty = draftFilters !== appliedFilters;
  const appliedCount = Object.values(appliedFilters).filter(
    (values) => values.length > 0
  ).length;

  const loadRows = useCallback(async () => {
    setIsLoading(true);

    const response = await getContestProductBalanceAdjustments(page, {
      contest_id: appliedFilters.contestIds,
      cp_classification_id: appliedFilters.productIds,
    });

    setIsLoading(false);

    if (!response.ok) return;

    const paginator = response.data?.response;
    setRows(paginator?.data ?? []);
    setPagination({
      current_page: paginator?.current_page ?? 1,
      last_page: paginator?.last_page ?? 1,
      total: paginator?.total ?? 0,
    });
  }, [page, appliedFilters]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    const loadProducts = async () => {
      if (draftFilters.contestIds.length === 0) {
        setProducts([]);
        return;
      }

      const response = await getBalanceAdjustmentProducts(draftFilters.contestIds);
      if (response.ok) setProducts(response.data?.response ?? []);
    };

    loadProducts();
  }, [draftFilters.contestIds]);

  const handleContestsChange = (contestIds: string[]) => {
    const allowedProducts = new Set(
      products
        .filter((product) => contestIds.includes(String(product.contest_id)))
        .map((product) => String(product.id))
    );

    setDraftFilters((current) => ({
      contestIds,
      productIds:
        contestIds.length === 0 ? [] : pruneSelection(current.productIds, allowedProducts),
    }));
  };

  const handleProductsChange = (productIds: string[]) => {
    setDraftFilters((current) => ({ ...current, productIds }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(1);
  };

  const handleClearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const handleCorrect = (row: BalanceAdjustmentRow) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  return (
    <div className="flex flex-col gap-4">
      <FiltersCard
        appliedCount={appliedCount}
        isDirty={isDirty}
        isLoading={isLoading}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        onRefresh={loadRows}
      >
        <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label>Concurso</Label>
            <MultiSelectCheckbox
              options={toOptions(contests)}
              selectedValues={draftFilters.contestIds}
              hideBadge
              onSelectionChange={handleContestsChange}
              placeholder="Todos os concursos"
              searchable
              searchPlaceholder="Buscar concurso..."
            />
          </div>

          <div className="space-y-1">
            <Label>Bolão</Label>
            <MultiSelectCheckbox
              options={toProductOptions(products, draftFilters.contestIds.length > 1)}
              selectedValues={draftFilters.productIds}
              hideBadge
              onSelectionChange={handleProductsChange}
              placeholder={
                draftFilters.contestIds.length === 0 ? "Selecione o concurso" : "Todos os bolões"
              }
              searchable
              searchPlaceholder="Buscar bolão..."
            />
          </div>
        </div>
      </FiltersCard>

      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bolão</TableHead>
              <TableHead>Concurso</TableHead>
              <TableHead className="text-right">Balanço atual</TableHead>
              <TableHead className="text-right">Valor preso atual</TableHead>
              <TableHead className="text-right">Ação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                </TableCell>
              </TableRow>
            )}

            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  Nenhum balanço encontrado para os filtros selecionados.
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              rows.map((row) => (
                <TableRow key={row.uuid}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>{row.product_name ?? "-"}</span>
                      <span className="text-xs text-muted-foreground">
                        {row.product_classification_name ?? ""}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>{row.contest_name ?? "-"}</span>
                      <span className="text-xs text-muted-foreground">
                        {row.lottery_name ?? ""}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary">{row.balance}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline">{row.with_held_balance}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => handleCorrect(row)}>
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      Corrigir
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        currentPage={pagination.current_page}
        lastPage={pagination.last_page}
        total={pagination.total}
        onChange={setPage}
      />

      <CorrectBalanceModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        type="contest_product"
        typeLabel="Balanço geral do concurso/bolão"
        record={selectedRow}
        recordLabel={[selectedRow?.product_name, selectedRow?.contest_name]
          .filter(Boolean)
          .join(" • ")}
        onSaved={loadRows}
      />
    </div>
  );
};
